export type MeetingStatus = 'scheduled' | 'in_progress' | 'completed' | 'cancelled'

export type SectionType =
  | 'set_the_stage'
  | 'check_in'
  | 'scoreboard'
  | 'rock_review'
  | 'accountability'
  | 'team_engagement'
  | 'business_development'
  | 'the_list'
  | 'projects'
  | 'check_out'
  | 'custom'

export type SectionStatus = 'pending' | 'in_progress' | 'completed' | 'skipped'

export interface User {
  id: number
  email: string
  first_name: string
  last_name: string
  title: string | null
  phone: string | null
  full_name: string
  initials: string
  role: 'observer' | 'member' | 'admin' | 'account_owner' | 'guide' | 'external_user'
  avatar_text: string | null
  avatar_url: string | null
  avatar_size: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  avatar_display: string
  is_executive_leadership: boolean
  is_temporary_admin?: boolean
  guide_user_privileges?: boolean
  unconfirmed_email: string | null
}

export interface Organization {
  id: number
  name: string
  slug?: string
}

export type BillingPayer = 'guide' | 'company'
export interface TeamMember {
  id: number
  first_name: string
  last_name: string
  initials: string
  role: string
}

export interface Team {
  id: number
  name: string
  description: string | null
  is_private: boolean
  member_count: number
  agenda_count: number
  current_user_role: string | null
  scoreboard_win_mode?: string
  scoreboard_win_threshold?: number
  scoreboard_week_label?: 'week_start' | 'week_ending'
  scoreboard_week_start_day?: WeekStartDay
  members?: TeamMember[]
  meeting_day?: string | null
  meeting_time?: string | null
}

export interface TeamMembership {
  id: number
  team_id: number
  user_id: number
  role: string
}

// Task #645: a Healthy Conflict answer is three-state. The server normalizes
// the legacy booleans stored on older meetings into these strings before
// serializing, so the frontend only ever handles the canonical values.
export type ConflictResponse = 'yes' | 'no' | 'na'

export interface Meeting {
  id: number
  status: MeetingStatus
  scheduled_date: string
  started_at: string | null
  ended_at: string | null
  current_phase: SectionType | null
  current_section_id?: number | null
  rating: number | null
  had_healthy_conflict: boolean | null
  duration_minutes: number | null
  team: Team | null
  team_name?: string | null
  facilitator: User
  agenda_template_id: number | null
  rating_visibility?: string
  ratings_revealed_at?: string | null
  average_rating?: number | null
  summary_sent_at?: string | null
  end_with_fun_gif?: boolean
  ratings_data?: Record<string | number, number>
  conflict_responses?: Record<string | number, ConflictResponse>
  communications?: WNTKEntry[]
  team_engagement_notes?: Record<string, string>
  business_development_notes?: Record<string, string>
  display_name?: string
  agenda_template_name?: string | null
  recap_skip_reason?: string | null
}

export interface WNTKEntry {
  id?: string
  who_id: number | string
  who_name?: string
  tells_whom: string
  about_what: string
}

export type TemplateStatus = 'active' | 'inactive'

export interface AgendaTemplateSummary {
  id: number
  name: string
  description: string | null
  meeting_type?: string | null
  total_duration_minutes: number
  section_count: number
}

export interface AvailableSection {
  section_type: SectionType
  display_name: string
  default_duration: number
}

// Task #607: individual Scoreboard notes that carry forward into every future
// meeting until someone deletes one. Other sections still use the free-text
// `notes` field below.
export interface MeetingSectionNote {
  id: number
  body: string
  position: number
  created_by_name: string | null
  created_at: string | null
}

export interface MeetingSection {
  id: number
  section_type: SectionType
  display_name: string
  title?: string
  position: number
  status: SectionStatus
  duration_minutes: number
  default_duration: number
  started_at: string | null
  completed_at: string | null
  elapsed_seconds: number
  timer_running: boolean
  notes: string | null
  section_notes?: MeetingSectionNote[]
  is_heading: boolean
}

export interface ActionItem {
  id: number
  title: string
  description: string | null
  status: 'open' | 'completed' | 'cancelled' | 'carried_over' | 'converted_to_topic'
  due_date: string
  completed_at: string | null
  overdue: boolean
  due_today: boolean
  source_type: string | null
  source_id: number | null
  meeting_id: number | null
  position: number | null
  owner: User
  created_by: User | null
  created_at: string
  updated_at: string
  checklist_items: ActionItemChecklistItem[]
  checklist_completed_count: number
  checklist_total_count: number
  carryovers?: ActionItemCarryover[]
}

export interface ActionItemCarryover {
  id: number
  carried_over_on: string
  meeting_id: number | null
}

export interface ActionItemChecklistItem {
  id: number
  action_item_id: number
  title: string
  status: 'pending' | 'complete'
  position: number
  completed_at: string | null
}

export interface ActionItemFilters {
  status?: string
  owner_id?: string
  search?: string
  due_date_range?: string
  sort?: string
  direction?: 'asc' | 'desc'
}

export interface ActionItemCounts {
  open: number
  completed: number
  overdue: number
}

export type TopicCategory =
  | 'obstacle_to_tackle'
  | 'info_to_share'
  | 'decision_required'
  | 'i_need_help'
  | 'opportunity'

export type TopicResolutionType =
  | 'decision_made'
  | 'done'
  | 'resolved'
  | 'convert_to_action'
  | 'tabled'

export interface Topic {
  id: number
  team_id: number
  title: string
  description: string | null
  status: 'open' | 'discussed' | 'resolved' | 'deferred'
  // Uncategorized topics carry null/'' and render no category badge.
  category: TopicCategory | null
  // Simple yes/no flag (old 1-5 levels collapsed to true).
  priority: boolean
  position: number
  votes_count: number
  resolution_notes: string | null
  resolution_type: string | null
  resolved_at: string | null
  resolved_in_meeting_id: number | null
  source_type: string | null
  source_id: number | null
  topic_type: 'short_term' | 'long_term'
  created_at: string
  updated_at: string
  owner: User | null
  created_by: User | null
  voted_by_current_user: boolean
  voter_names: string[]
}

export const TOPIC_CATEGORIES: { value: TopicCategory; label: string; color: string }[] = [
  { value: 'obstacle_to_tackle', label: 'Obstacle or Issue', color: '#ED1C24' },
  { value: 'info_to_share', label: 'Share Information', color: '#648E8A' },
  { value: 'decision_required', label: 'Need a Decision', color: '#1B1B1B' },
  { value: 'i_need_help', label: 'Need Help', color: '#B87A20' },
  { value: 'opportunity', label: 'Opportunity', color: '#2F8F5E' },
]

// Every category exposes the same in-meeting actions. Resolution variants
// like "Decision Made" / "Done" / "Table for Later" are collapsed into the
// single "Resolved" action; Delete is rendered separately, not from this map.
const UNIFIED_RESOLUTIONS: { value: TopicResolutionType; label: string }[] = [
  { value: 'resolved', label: 'Resolved' },
  { value: 'convert_to_action', label: 'Convert to Action Item' },
]

export const CATEGORY_RESOLUTION_MAP: Record<TopicCategory, { value: TopicResolutionType; label: string }[]> = {
  decision_required: UNIFIED_RESOLUTIONS,
  info_to_share: UNIFIED_RESOLUTIONS,
  obstacle_to_tackle: UNIFIED_RESOLUTIONS,
  i_need_help: UNIFIED_RESOLUTIONS,
  opportunity: UNIFIED_RESOLUTIONS,
}

export type TopicSort = 'manual' | 'name' | 'owner' | 'priority' | 'votes' | 'date_created'


export interface TopicFilters {
  tab: string
  search: string | null
  category: string | null
  state: string | null
  sort: TopicSort
  solved_range?: string | null
  topic_type?: string | null
  // 'true' limits the list to priority topics; null/undefined shows all.
  priority?: string | null
  // 'list' (default) or 'grid' — how topics are displayed.
  view?: string | null
}

export interface TabCounts {
  active: number
  solved: number
  short_term?: number
  long_term?: number
}

export interface UserSummary {
  id: number
  first_name: string
  last_name: string
  initials?: string
}

export interface Rock {
  id: number
  team_id: number
  owner_id: number
  owner: User
  title: string
  description: string | null
  why_important: string | null
  resources_needed: string | null
  status: 'on_track' | 'off_track' | 'at_risk' | 'complete' | 'dropped'
  quarter: string | null
  due_date: string | null
  progress_type: 'status_only' | 'status_with_target' | 'from_project'
  target_value: number | null
  starting_value: number | null
  current_value: number | null
  target_type: 'number' | 'currency' | 'percentage' | null
  is_company_rock: boolean
  category_id: number | null
  category: RockCategory | null
  state: 'active' | 'backlog' | 'archived'
  position: number
  percent_complete: number
  last_check_in_at: string | null
  last_check_in_note: string | null
  milestones: RockMilestone[]
  milestones_completed_count: number
  milestones_total_count: number
  action_items_count: number
  check_ins: RockCheckInRecord[]
  progress_percentage: number | null
  can_delete?: boolean
  can_edit?: boolean
  created_at: string
  updated_at: string
}

export interface RockMilestone {
  id: number
  rock_id: number
  title: string
  description: string | null
  assignee_id: number | null
  assignee: User | null
  due_date: string | null
  status: 'pending' | 'complete'
  position: number
  completed_at: string | null
  linked_action_item_id?: number | null
}

export interface RockCategory {
  id: number
  team_id: number
  name: string
  position: number
}

export interface RockCheckInRecord {
  id: number
  rock_id: number
  user_id: number
  user: User
  status: 'on_track' | 'off_track' | 'at_risk' | null
  percent_complete: number | null
  value: number | null
  note: string | null
  meeting_id: number | null
  created_at: string
}

export interface RockFilters {
  search?: string
  tab?: 'active' | 'backlog'
  state?: 'active' | 'archived'
  due_date?: string
  sort_by?: string
  group_by?: 'owner' | 'category' | 'none'
}

export interface Metric {
  id: number
  name: string
  target: number | null
  actual: number | null
  unit: string | null
  frequency: string | null
  goal_direction: string | null
  on_track: boolean
  owner: User | null
}

export interface CheckInData {
  id: number
  // Null while a cell is being cleared inline (task #596); the server only
  // ever serializes a number for a persisted entry.
  value: number | null
  context: string | null
  notes: string | null
  variable_target: number | null
  recorded_at: string
  on_track: boolean | null
  won_the_week: boolean | null
  won_the_week_manual?: boolean
}

export interface MetricWithEntries {
  id: number
  name: string
  description: string | null
  target: number | null
  actual: number | null
  unit: string | null
  frequency: string | null
  goal_direction: string | null
  format: string | null
  comparison: string | null
  state: string | null
  status: string | null
  position: number
  category_id: number | null
  decimal_places: number
  abbreviate_large_numbers: boolean
  formatted_target: string
  ytd_total: number
  on_track: boolean | null
  owner: User | null
  category: { id: number; name: string } | null
  entries: Record<string, CheckInData>
}

// Minimal shape the Edit Metric modal needs. MetricWithEntries (Team
// Scoreboard) satisfies it structurally; the meeting scoreboard builds one
// from a MeetingMetric so both surfaces share the same modal.
export interface EditableMetric {
  id: number
  name: string
  description?: string | null
  owner?: { id: number; full_name?: string } | null
  frequency?: string | null
  format?: string | null
  comparison?: string | null
  target?: number | null
  category_id?: number | null
  decimal_places?: number
  abbreviate_large_numbers?: boolean
}

export interface MetricCategory {
  id: number
  name: string
  position: number
}

export interface DateColumn {
  key: string
  label: string
  start_date: string
  end_date: string
  is_current: boolean
}

export interface FilterParams {
  cadence: string
  state: string
  search: string
  weeks: number
}

export interface MeetingAttendee {
  id: number
  attending: boolean
  position?: number | null
  user: User
}

export interface OrganizationSettings {
  auto_complete_enabled: boolean
  auto_complete_after_hours: number
  send_recap_email: boolean
  include_ratings_in_email: boolean
  include_section_durations: boolean
}

export const SECTION_DISPLAY_NAMES: Record<SectionType, string> = {
  set_the_stage: 'Set the Stage',
  check_in: 'Check-In',
  scoreboard: 'Scoreboard',
  rock_review: 'Rock Review',
  accountability: 'Accountability from Last Week',
  team_engagement: 'Team Engagement',
  business_development: 'Business Development',
  the_list: 'The List of Topics',
  check_out: 'Check-Out',
}

export const MEETING_RULES: string[] = [
  'Be early, be ready, be all in',
  'Alignment starts with listening, not talking',
  'Create clarity, not noise when speaking',
  'Every word should elevate the work',
  'Push rocks forward with purpose and urgency',
]

export interface TeamSummary {
  id: number
  name: string
  member_count: number
}

export type ImpersonationMode = 'company' | 'guide'

export interface Impersonation {
  active: true
  mode: ImpersonationMode
  organization: { id: number; name: string } | null
  guide?: { id: number; email: string; full_name: string }
  super_admin: { id: number; email: string; full_name: string }
  stop_path: string
}

export interface OrganizationOption {
  id: number
  name: string
  slug: string
  user_id: number
  active: boolean
}

export interface SharedProps {
  current_user: User | null
  can_access_guide_billing: boolean
  can_access_company_billing?: boolean
  company_payment_handoff?: import('../components/billing/CompanyHandoff').CompanyHandoffData | null
  user_teams: TeamSummary[]
  current_team: TeamSummary | null
  flash: {
    notice?: string
    alert?: string
    // Set by RocksController#create so the Rock Planner can bind to the rock
    // its first autosave created.
    created_rock_id?: number | null
    // Set by SeatsController#restore so chart undo can bind its history entry
    // to the new id of the box it just put back.
    restored_seat_id?: number | null
    // How many of that box's comments were re-attached to it, so the notice
    // can say the conversation came back too.
    restored_comment_count?: number | null
    // Set by SeatsController#create and #duplicate so chart undo can take a
    // newly added box back by deleting the id it was given.
    created_seat_id?: number | null
  }
  available_organizations: OrganizationOption[]
  current_organization: Organization | null
  published_charts: PublishedChartLink[]
  /** Legacy name: chart-list read access only; never use to authorize edits. */
  can_manage_charts: boolean
  account_id: number | null
  csrf_token: string
  // Two separate Super Admin modes share this prop:
  //   company — hidden support identity, locked to one company
  //   guide   — acting as a real Guide, who may have zero, one, or many
  //             companies (so `organization` can be null)
  impersonation?: Impersonation | null
}

export interface PublishedChartLink {
  id: number
  name: string
}

export interface PageProps {
  flash?: {
    notice?: string
    alert?: string
  }
  current_user?: User
}

export interface MeetingStats {
  completed_this_quarter: number
  total_weeks_in_quarter: number
  next_meeting: {
    id: number
    scheduled_date: string
    facilitator: { first_name: string; last_name: string } | null
  } | null
  last_meeting: {
    id: number
    ended_at: string
    rating: number | null
    average_rating: number | null
  } | null
  meeting_in_progress: {
    id: number
    started_at: string
    template_name: string | null
    facilitator: { first_name: string; last_name: string } | null
    attendees: { id: number; first_name: string; last_name: string }[]
  } | null
  streak_count: number
  has_any_meetings: boolean
}

export interface ScoreboardStats {
  total_metrics: number
  on_track_count: number
  off_track_count: number
  highlighted_metrics: HighlightedMetric[]
}

export interface HighlightedMetric {
  id: number
  name: string
  owner: UserSummary | null
  target: number | null
  current_value: number | null
  unit: string | null
  format: string
  decimal_places: number | null
  on_track: boolean | null
  goal_direction: string
}

export interface RocksStats {
  quarter_label: string
  total: number
  count_at_100: number
  average_percent: number
  rocks_list: RockSummary[]
}

export interface RockSummary {
  id: number
  title: string
  percent_complete: number
  owner: UserSummary | null
  due_date: string | null
}

export interface ActionItemStats {
  open_count: number
  carried_over_count: number
  overdue_count: number
  completed_this_week: number
  completion_rate: number
  urgent_items: ActionItemSummary[]
}

export interface ActionItemSummary {
  id: number
  title: string
  due_date: string | null
  overdue: boolean
  owner: UserSummary | null
}

export interface TopicStats {
  open_count: number
  resolved_this_week: number
  top_topics: TopicSummary[]
}

export interface TopicSummary {
  id: number
  title: string
  votes_count: number
  priority: boolean
  category: string | null
  owner: UserSummary | null
}

export interface TeamMember {
  id: number
  first_name: string
  last_name: string
  initials: string
  role: string
}

export interface AgendaTemplateSection {
  id: number
  agenda_template_id: number
  section_type: string
  title: string
  original_title: string | null
  duration_minutes: number | null
  position: number
  is_enabled: boolean
  is_system_section: boolean
  is_heading: boolean
  custom_notes: string | null
}

export interface OrgTeamOption {
  id: number
  name: string
  member_count: number
}

export interface AgendaTemplate {
  id: number
  name: string
  original_name?: string | null
  description: string | null
  meeting_type: string
  total_duration_minutes: number
  is_required?: boolean
  required_kind?: string | null
  can_delete?: boolean
  sections: AgendaTemplateSection[]
  org_teams?: OrgTeamOption[]
  assigned_team_ids?: number[]
}

export interface CascadingMessage {
  id: number
  title: string
  details: string | null
  due_date: string | null
  status: 'pending' | 'sent'
  created_by: { id: number; name: string }
  assignees: { id: number; name: string; initials: string }[]
  target_teams: { id: number; name: string }[]
  created_at: string
}

export interface MeetingRating {
  id: number
  user_id: number
  user_name: string
  user_initials: string
  rating: number
  comment: string | null
  comment_visibility: 'public' | 'private'
  is_absent: boolean
  created_at: string
}

export type EmailDeliveryMode = 'off' | 'test_only' | 'live'

export interface SystemEmailSetting {
  id: number | null
  provider: string
  from_email: string | null
  from_name: string | null
  guide_from_email: string | null
  guide_from_name: string | null
  reply_to_email: string | null
  summary_template_id: string | null
  delivery_mode: EmailDeliveryMode
  configured: boolean
  normal_delivery_allowed: boolean
  operator_tests_allowed: boolean
  api_key_present: boolean
  webhook_signing_key_present: boolean
  last_test_sent_at: string | null
  last_test_sent_display: string | null
  last_signed_webhook_display: string | null
  updated_at_display: string | null
}

export interface EmailTestCatalogEntry {
  key: string
  label: string
  description: string
  group: string
  group_label: string
  delivery_path: string
  source: string
  audience: 'client' | 'guide'
}

export interface EmailTestMessageResult {
  template_revision_label?: string
  id: number
  catalog_key: string
  label: string
  group_label: string | null
  status: string
  status_label: string
  status_tone: 'pending' | 'accepted' | 'success' | 'failure' | 'suppressed'
  status_detail: string
  awaiting_confirmation: string | null
  rendering_mode: string | null
  rendering_mode_label: string | null
  delivery_path: string | null
  delivery_path_label: string
  sender_audience: 'client' | 'guide'
  sender_email: string | null
  sender_name: string | null
  error_message: string | null
  provider_message_id: string | null
  attempts: number
  retryable: boolean
  last_event_type: string | null
  updated_at_display: string | null
  accepted_at_display: string | null
  delivered_at_display: string | null
}

export interface EmailTestBatchResult {
  id: number
  recipient_email: string
  created_at_display: string | null
  in_progress: boolean
  messages: EmailTestMessageResult[]
}

export interface EmailReadinessCheck {
  name: string
  status: string
  detail: string
}

export interface EmailReadinessSection {
  status: string
  detail: string
  checks?: EmailReadinessCheck[]
  at_display?: string | null
  host?: string | null
}

export interface EmailReadinessPrerequisite {
  name: string
  status: string
  detail: string
  instructions: string
}

export interface EmailReadiness {
  delivery_mode: EmailDeliveryMode
  saved_configuration: EmailReadinessSection
  guide_sender: EmailReadinessSection
  provider_acceptance: EmailReadinessSection
  local_webhook: EmailReadinessSection
  inbound_signed_traffic: EmailReadinessSection
  worker_processing: EmailReadinessSection
  public_host: EmailReadinessSection
  external_prerequisites: EmailReadinessPrerequisite[]
}

export interface EmailEvent {
  id: number
  event_type: string
  recipient_email: string
  subject: string | null
  reason: string | null
  occurred_at: string
}

export type UserRole = 'observer' | 'member' | 'admin' | 'account_owner' | 'guide' | 'external_user'
export type UserStatus = 'active' | 'deactivated' | 'deleted'

export interface PersonSummary {
  id: number
  first_name: string
  last_name: string
  full_name: string
  email: string
  title: string | null
  role: UserRole
  status: UserStatus
  avatar_text: string | null
  avatar_url: string | null
  initials: string
  is_executive_leadership: boolean
  guide_user_privileges: boolean
  manager_id: number | null
  manager_name: string | null
  teams: { id: number; name: string }[]
  direct_reports_count: number
  deactivated_at: string | null
  last_activity_at: string | null
  created_at: string
  /** Only served by the People list: does this person own a role on the Results Ownership Chart? */
  on_chart?: boolean
  /** Names of the chart roles this person owns (empty when they own none). */
  chart_seat_names?: string[]
}

/** One role on the Results Ownership Chart, with the permissions the placement form gates on. */
export interface PlacementSeat {
  id: number
  name: string
  parent_seat_id: number | null
  seat_status: string
  is_owners_box: boolean
  owner_names: string[]
  can_edit: boolean
  can_add_child: boolean
}

/**
 * The organization's single active Results Ownership Chart, as the People page
 * sees it. `seats` is empty when the viewer may not place anyone on it.
 */
export interface ResultsOwnershipChartPlacement {
  id: number
  name: string
  can_add_root: boolean
  can_place: boolean
  seats: PlacementSeat[]
}

export interface PersonDetail extends PersonSummary {
  phone: string | null
  has_hr_permission: boolean
  team_memberships: { team_id: number; team_name: string; role: string }[]
}

export interface PendingInvitation {
  id: number
  email: string
  role: string
  status: string
  invited_by_name: string
  team_ids: number[]
  created_at: string
  expires_at: string
}

export interface DeletionManifest {
  rocks: { id: number; title: string; status: string; percent_complete?: number; quarter: string | null }[]
  action_items_open: { id: number; title: string; due_date: string | null }[]
  action_items_completed_count: number
  metrics: { id: number; name: string }[]
  topics_open: { id: number; title: string }[]
  team_memberships: { team_id: number; team_name: string; role: string }[]
  direct_reports: { id: number; name: string }[]
  meetings_facilitated_count: number
  tenants: { organization_id: number; organization_name: string | null; role: string; is_current: boolean }[]
  is_multi_tenant: boolean
}

export interface PeopleCounts {
  active: number
  pending: number
  deactivated: number
  deleted: number
  total: number
}

export const ROLE_LABELS: Record<UserRole, string> = {
  observer: 'Observer',
  member: 'Member',
  admin: 'Admin',
  account_owner: 'Account Owner',
  guide: 'Guide',
  external_user: 'External User',
}

export const ROLE_OPTIONS: { value: UserRole; label: string }[] = [
  { value: 'member', label: 'Member' },
  { value: 'admin', label: 'Admin' },
  { value: 'account_owner', label: 'Account Owner' },
  { value: 'observer', label: 'Observer' },
  { value: 'guide', label: 'Guide' },
  { value: 'external_user', label: 'External User' },
]

export function roleOptionsForAssignment(
  assignableRoles: string[],
  currentRole?: UserRole
): { value: UserRole; label: string }[] {
  const options = ROLE_OPTIONS.filter(o => assignableRoles.includes(o.value))
  if (!currentRole || options.some(o => o.value === currentRole)) return options
  const current = ROLE_OPTIONS.find(o => o.value === currentRole)
  return current ? [current, ...options] : options
}
export type ScoreboardStatus = 'win' | 'close' | 'miss' | 'no_data'

export interface ScoreboardMetric {
  id: number
  name: string
  target: number | null
  value: number | null
  unit: string | null
  format: string | null
  goal_direction: string | null
  status: ScoreboardStatus
  owner_name: string | null
}

export interface PrioritiesSummary {
  total: number
  count_at_100: number
  average_percent: number
}

export interface RecentMeeting {
  id: number
  name: string
  scheduled_date: string | null
  status: MeetingStatus
  team_id: number | null
}

export interface BriefUser {
  id: number
  first_name: string
  last_name: string
  initials: string
}

export interface MetricWeekEntry {
  week_start: string
  week_ending: string
  // Server-built column label for non-weekly cadences (e.g. "Jan 2026",
  // "Q1 2026", "2026"). Absent/null on weekly, which formats client-side.
  label?: string | null
  value: number | null
  on_target: boolean | null
  entry_id: number | null
  won_the_week: boolean | null
  won_the_week_manual?: boolean
}

export interface MeetingMetric {
  id: number
  name: string
  description?: string | null
  frequency?: string | null
  category_id?: number | null
  target?: number | null
  owner: BriefUser
  target_value: number | null
  unit: string
  format?: string | null
  decimal_places?: number
  abbreviate_large_numbers?: boolean
  goal_direction: 'higher_is_better' | 'lower_is_better' | 'target_exact'
  comparison: 'gte' | 'lte' | 'gt' | 'lt' | 'eq' | null
  current_value: number | null
  on_target: boolean | null
  ytd_total: number
  weekly_entries: MetricWeekEntry[]
}

export interface ScoreboardSectionProps {
  metrics: MeetingMetric[]
  all_metrics_count: number
  won_the_week: boolean | null
  win_count: number
  total_count: number
  // How many weekly metrics the current period expects a contribution from,
  // and whether every one of them has a value or a manual Won/Lost override.
  // The automatic Win-the-Week verdict is undetermined (and the banner
  // hidden) until then — distinct from a team with no weekly metrics at all,
  // which keeps the neutral banner. Both are 0/false on non-weekly tabs.
  weekly_metric_count?: number
  weekly_metrics_complete?: boolean
  win_mode: 'automatic' | 'manual'
  win_threshold: number
  meeting_id?: number
  can_facilitate?: boolean
  team_id?: number
  can_create_metric?: boolean
  can_update_metric?: boolean
  // Reordering is a team-level permission (TeamPolicy#update?), not the
  // per-metric edit permission — see the meeting scoreboard's drag handle.
  can_reorder_metric?: boolean
  categories?: MetricCategory[]
  scoreboard_week_label?: 'week_start' | 'week_ending'
  scoreboard_week_start_day?: WeekStartDay
  cadence?: ScoreboardCadence
  period_count?: number
}

export type ScoreboardCadence = 'weekly' | 'monthly' | 'quarterly' | 'annual'

export type WeekStartDay = 'sunday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday'

export interface GuideDashboardMeeting {
  id: number
  name: string
  scheduled_date: string | null
  status: MeetingStatus
  team_id: number | null
  team_name: string | null
}

export interface GuideDashboardRock {
  id: number
  title: string
  percent_complete: number
  due_date: string | null
  overdue: boolean
  team_id: number | null
  team_name: string | null
}

export interface GuideDashboardActionItem {
  id: number
  title: string
  due_date: string | null
  overdue: boolean
  due_today: boolean
  team_id: number | null
  team_name: string | null
}

export interface GuideDashboardMyActionItem {
  id: number
  title: string
  due_date: string | null
  overdue: boolean
  due_today: boolean
  carried_over: boolean
  organization_id: number
  organization_name: string | null
  team_id: number | null
  team_name: string | null
}

export interface GuideDashboardMyTopic {
  id: number
  title: string
  status: string
  priority: boolean
  organization_id: number
  organization_name: string | null
  team_id: number | null
  team_name: string | null
}

export interface GuideDashboardMyRock {
  id: number
  title: string
  percent_complete: number
  due_date: string | null
  overdue: boolean
  organization_id: number
  organization_name: string | null
  team_id: number | null
  team_name: string | null
}

export type GuideDashboardHealth = 'on_track' | 'at_risk' | 'off_track'

export interface GuideDashboardScoreboard {
  on_track: number
  off_track: number
  no_data: number
  total: number
  percent_on_track: number | null
}

export interface GuideDashboardAccount {
  organization: { id: number; name: string; slug: string }
  user_id: number
  is_current: boolean
  role: UserRole
  upcoming_meetings: GuideDashboardMeeting[]
  at_risk_rocks: GuideDashboardRock[]
  open_action_items: GuideDashboardActionItem[]
  scoreboard: GuideDashboardScoreboard
  health: GuideDashboardHealth
}

export interface GuideDashboardProps {
  accounts: GuideDashboardAccount[]
  my_action_items: GuideDashboardMyActionItem[]
  my_topics: GuideDashboardMyTopic[]
  my_rocks: GuideDashboardMyRock[]
  totals: {
    organizations: number
    upcoming_meetings: number
    at_risk_rocks: number
    open_action_items: number
    my_open_action_items: number
    my_open_topics: number
    my_active_rocks: number
    metrics_on_track: number
    metrics_off_track: number
  }
}

export interface HomeProps {
  user: { first_name: string; last_name: string }
  scoreboard: ScoreboardMetric[]
  recent_meetings: RecentMeeting[]
  team_id: number | null
  team_agenda_name: string | null
  weekly_tactical_template: { id: number; name: string } | null
  priorities_summary: PrioritiesSummary
}

export interface TopicResolution {
  topic_id: number
  topic_title: string
  category?: string
  topic_type?: 'short_term' | 'long_term'
  owner_name?: string | null
  resolution_type: string
  resolution_notes: string | null
  status?: string
}

export interface BriefUser {
  id: number
  first_name: string
  last_name: string
  initials: string
}

export interface Milestone {
  title: string
  target_date: string | null
  completed: boolean
  is_current: boolean
}

export interface MeetingRock {
  id: number
  title: string
  description: string | null
  owner: BriefUser
  quarter: string
  milestones: Milestone[]
  current_milestone_index: number
  completed_milestone_count: number
  total_milestone_count: number
}

export interface RockReviewSectionProps {
  priorities: MeetingRock[]
  quarter_label: string
  quarter_end_date: string
}

export interface BillingSubscription {
  presentation?: import('../components/billing/BillingPresentation').BillingPresentation | null
  enrollment_allowed?: boolean
  trial_days?: number | null
  trial_consumed?: boolean
  subscription_confirmed?: boolean
  access_read_only?: boolean
  id?: number
  organization_id: number
  organization_name: string
  organization_slug?: string
  plan: BillingPlan
  payer: BillingPayer
  status: BillingStatus
  status_detail?: string | null
  next_charge_display?: string | null
  trial_ends_display?: string | null
  trial_requested?: boolean
  trial_decision?: 'trial' | 'immediate'
  people_count?: number
  payment_method_display?: string | null
  guide_name?: string | null
  invoices?: BillingInvoice[]
  scheduled_plan?: BillingPlan | null
  scheduled_plan_effective_display?: string | null
  payer_switch_effective_at: string | null
  payer_switch_effective_display: string | null
  pending_payer_switch?: {
    from: BillingPayer
    to: BillingPayer
    created_at_display?: string
    effective_at_display?: string | null
    payment_setup_ready?: boolean
    setup_completed?: boolean
    execution_stage?: string | null
    plan_key?: string | null
  } | null
}

export interface BillingPlan {
  key: string
  name: string
  price_monthly: number | null
  price_display?: string | null
  employee_band?: string
  band?: string
  configured?: boolean
}

export interface BillingInvoice {
  id: string | number
  date_display: string
  description?: string
  amount_display: string
  status: string
  hosted_url?: string | null
}

export type BillingStatus = 'active' | 'trialing' | 'past_due' | 'declined' | 'canceled' | 'paused' | 'incomplete' | 'no_payment'
